/**
 * Minecraft Theme - Centralized color palette and design tokens
 */

export const theme = {
  colors: {
    /* Softer background colors */

    // Primary colors
    primary: '#22c55e',
    primaryLight: '#17dd62',
    primaryDark: '#166534',

    // Secondary colors
    secondary: '#8b5cf6',
    secondaryLight: '#a78bfa',
    secondaryDark: '#7c3aed',

    // Accent colors (Tailwind palette)
    accent: {
      // Yellow
      yellow: '#eab308', // yellow-500
      yellowLight: '#fef9c3', // yellow-100
      yellow300: '#FCD34D', // yellow-300
      yellow700: '#A16207', // yellow-700
      yellow800: '#92400E', // yellow-800
      yellow900: '#78350F', // yellow-900
      // Orange
      orange: '#f97316', // orange-500
      orangeLight: '#fed7aa', // orange-200
      orange700: '#C2410C', // orange-700
      // Blue
      blue: '#3b82f6', // blue-500
      blue600: '#2563eb', // blue-600
      blueLight: '#dbeafe', // blue-100
      blue700: '#1D4ED8', // blue-700
      // Indigo
      indigo500: '#6366f1', // indigo-500
      indigo600: '#4f46e5', // indigo-600
      // Purple
      purple500: '#8b5cf6', // purple-500 (violet)
      purple700: '#7c3aed', // purple-700
      // Sky/Cyan
      sky400: '#38BDF8', // sky-400
      sky500: '#0EA5E9', // sky-500
      cyan: '#5decf5',
      cyan500: '#06B6D4', // cyan-500
      cyan700: '#0E7490', // cyan-700
      // Emerald / Green
      emerald500: '#10B981', // emerald-500
      emerald600: 'rgb(5 150 105)',
      emerald700: '#047857', // emerald-700
      green400: '#4ade80', // green-400
      green500: '#22c55e', // green-500
      // Teal
      teal500: '#14b8a6', // teal-500
      // Red
      red500: '#EF4444', // red-500
      red600: '#dc2626', // red-600
      red700: '#B91C1C', // red-700
      // Rose
      rose500: '#f43f5e', // rose-500
      // Pink
      pink: '#ec4899', // pink-500
      pinkLight: '#fce7f3', // pink-100
      // Additional palette for topic gradients
      purple400: '#c084fc', // purple-400
      purple600: '#9333ea', // purple-600
      purple800: '#7e22ce', // purple-800
      rose400: '#fb7185', // rose-400
      rose600: '#e11d48', // rose-600
      rose800: '#be123c', // rose-800
      teal300: '#2dd4bf', // teal-300
      teal700: '#0d9488', // teal-700
      teal800: '#0f766e', // teal-800
      amber400: '#fbbf24', // amber-400
      amber500: '#f59e0b', // amber-500
      amber600: '#d97706', // amber-600
      amber700: '#b45309', // amber-700
      blue400: '#60a5fa', // blue-400
      green600: '#16a34a', // green-600
      green700: '#15803d', // green-700
      green800: '#0ea34e', // green-800 (admin stats gradient)
      orange300: '#fdba74', // orange-300
      orange500Pure: '#FFA500', // orange-500 (pure orange, rank/trophy gradient)
      deepOrange700: '#F57C00', // deep-orange-700 (badge gradient)
      deepOrange900: '#E65100', // deep-orange-900 (badge border shadow)
      emerald800: '#1e8a60', // emerald-800 (milestone button shadow)
      lightBlue400: '#4facfe', // light-blue-400 (profile card gradient)
      cyan300: '#00f2fe', // cyan-300 (profile card gradient)
      purple700Soft: '#7c6be6', // purple soft (sky→blue→purple gradient endpoint)
      purple800Soft: '#764ba2', // purple-800 soft (admin stats gradient)
      pink300: '#f093fb', // pink-300 (admin stats gradient)
      rose400Soft: '#f5576c', // rose-400 soft (admin stats gradient)
      amber300: '#FBC02D', // amber-300 (badge/button gradient)
      gray400: '#9CA3AF', // gray-400 (silver gradient midpoint)
    },

    // Status colors
    success: '#22c55e',
    successLight: '#dcfce7',
    successDark: '#166534',
    error: '#dc2626',
    errorLight: '#fecaca',
    errorDark: '#991b1b',
    errorBright: '#ff4444', // brighter red for error states
    warning: '#f97316',
    warningLight: '#fed7aa',

    // Neutral colors
    text: {
      primary: '#2d2d2d',
      secondary: '#666666',
      muted: '#9ca3af',
      white: '#ffffff',
      gray400: '#9CA3AF', // gray-400
      gray600: '#4B5563', // gray-600
      gray800: '#1F2937', // gray-800
    },

    // Background colors
    background: {
      primary: '#ffffff',
      secondary: '#f3f4f6',
      tertiary: '#e5e7eb',
      green: '#d1fae5',
      greenLight: '#e8f5e9', // very light green for profile headers
      greenGradient: 'linear-gradient(to bottom, #e2edd8 0%, #cddfc2 100%)', // soft green gradient for profile footer
      yellow: '#fef9c3',
      coral: '#fecaca',
      lavender: '#c7d2fe',
      blue: '#dbeafe',
      wood: '#e8dcc4',
      woodPale: '#f5f0e6', // pale wood background for stat cards
      slate50: '#f8fafc', // slate-50 for subtle backgrounds
      slate800: '#1e293b', // slate-800 for skip-link/dark UI elements
      gray100: '#d1d5db', // gray-300 equivalent
      gray200: '#b8b4a8', // warm gray for unearned badge backgrounds
      darkNavy: '#1a1a2e', // dark navy for admin/dark panels
      darkNavyBorder: '#2a2a3e', // dark navy border
      midNight: '#16213e', // midnight blue for admin tabs
      diamondBlock:
        'linear-gradient(135deg, rgba(93, 236, 245, 0.15) 0%, rgba(93, 236, 245, 0.08) 100%)',
      woodGradient: 'linear-gradient(to bottom, #f5f1e8 0%, #e8dfc8 100%)',
      woodDarkGradient: 'linear-gradient(to bottom, #ede5d3 0%, #e4dcc8 100%)',
      stoneBlock:
        'linear-gradient(135deg, rgba(127, 127, 127, 0.05) 0%, rgba(127, 127, 127, 0.02) 100%)',
    },

    // Navigation colors
    nav: {
      activeBackground: '#5dab8a',
      inactiveBackground: '#ffffff',
      activeText: '#ffffff',
      inactiveText: '#666666',
    },

    // Border colors
    border: {
      light: '#e5e7eb',
      medium: '#9ca3af',
      dark: '#2d2d2d',
    },

    // Overlay / glass colors (rgba values as strings)
    overlay: {
      dark50: 'rgba(0, 0, 0, 0.5)',
      dark70: 'rgba(0, 0, 0, 0.7)',
      black10: 'rgba(0, 0, 0, 0.1)',
      white10: 'rgba(255, 255, 255, 0.1)',
      white15: 'rgba(255, 255, 255, 0.15)',
      white20: 'rgba(255, 255, 255, 0.2)',
      white30: 'rgba(255, 255, 255, 0.3)',
      white40: 'rgba(255, 255, 255, 0.4)',
      white50: 'rgba(255, 255, 255, 0.5)',
      white60: 'rgba(255, 255, 255, 0.6)',
      white70: 'rgba(255, 255, 255, 0.7)',
      white80: 'rgba(255, 255, 255, 0.8)',
      white85: 'rgba(255, 255, 255, 0.85)',
      white90: 'rgba(255, 255, 255, 0.9)',
      white95: 'rgba(255, 255, 255, 0.95)',
      yellow60: 'rgba(254, 249, 195, 0.6)', // yellow-100 at 60% opacity (admin badge/status)
      gold10: 'rgba(255, 215, 0, 0.1)', // gold at 10% opacity (activity feed)
      wood85: 'rgba(232, 220, 196, 0.85)', // wood color at 85% opacity (header bg)
      green10: 'rgba(23, 221, 98, 0.1)', // primaryLight at 10% opacity (spinner border)
      indigo10: 'rgba(102, 126, 234, 0.1)', // indigoViolet at 10% opacity (activity feed)
      white5: 'rgba(255, 255, 255, 0.05)', // white at 5% opacity (activity feed default)
      cyan700_10: 'rgba(14, 116, 144, 0.1)', // cyan700 at 10% opacity
      emerald10: 'rgba(16, 185, 129, 0.1)', // emerald500 at 10% opacity
      success10: 'rgba(34, 197, 94, 0.1)', // green500/success at 10% opacity
      error10: 'rgba(220, 38, 38, 0.1)', // error/red600 at 10% opacity
      error8: 'rgba(220, 38, 38, 0.08)', // error/red600 at 8% opacity
      orange15: 'rgba(249, 115, 22, 0.15)', // orange500 at 15% opacity
      blue15: 'rgba(59, 130, 246, 0.15)', // blue500 at 15% opacity
    },

    // Semantic color for emerald green milestone/stats highlights
    emeraldGreen: '#2ea87a',

    // Indigo/violet for activity feed (user_registered events)
    indigoViolet: '#667eea',
  },

  // Topic colors for cards
  topicColors: {
    addition: '#22c55e',
    subtraction: '#3b82f6',
    multiplication: '#f97316',
    division: '#8b5cf6',
    fractions: '#ec4899',
    decimals: '#eab308',
    default: '#22c55e',
  },

  // Badge colors
  badgeColors: {
    quickThinker: '#eab308',
    mathWizard: '#8b5cf6',
    streakMaster: '#ef4444',
    perfectScore: '#22c55e',
  },

  // Leaderboard rank colors
  rankColors: {
    gold: '#FFD700',
    silver: '#C0C0C0',
    bronze: '#CD7F32',
  },

  // Difficulty colors
  difficultyColors: {
    easy: '#22c55e',
    medium: '#eab308',
    hard: '#ef4444',
  },

  // Spacing scale
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    xxl: '24px',
    xxxl: '32px',
  },

  // Font sizes
  fontSize: {
    xs: '12px',
    sm: '13px',
    md: '14px',
    lg: '16px',
    xl: '20px',
    xxl: '24px',
    xxxl: '28px',
  },

  // Border radius
  borderRadius: {
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
  },

  // Shared shadows and effects
  shadows: {
    card: '0 2px 8px rgba(0, 0, 0, 0.06)',
    cardHover: '0 4px 12px rgba(0, 0, 0, 0.1)',
    button: '0 4px 12px rgba(0, 0, 0, 0.15)',
    buttonBorder: '4px solid rgba(0, 0, 0, 0.2)',
  },
} as const;

// Type exports for TypeScript
export type Theme = typeof theme;
export type ThemeColors = typeof theme.colors;
export type TopicColor = keyof typeof theme.topicColors;
export type DifficultyColor = keyof typeof theme.difficultyColors;
